/**
 * 退房
 */
import React, { useEffect, useRef, memo, useState } from 'react';
import { connect } from 'react-redux';
import { message, Spin, Button } from 'antd';
import intl from 'react-intl-universal';

import usePortalDrawer, { DrawerHeader, DrawerFooter } from '@/components/PortalDrawer';
import usePortalConfirm from '@/components/PortalConfirm';
import PortalLoading from '@/components/PortalLoading';
import {
  showWardRoundConfirm,
  showRoomCardConfirm,
  showSettlePrompt,
  showCheckOutPrompt,
} from '@/components/Reservation/Modal';
import checkInService from '@/api/checkIn';
import { parseYuan2Fen } from '@/utils/number';
import { parseSeconds2Hour } from '@/utils/date';

import { createStateSelector } from '@/redux/actions/reservation/helper';
import {
  initPageState,
  setLoadingStatus,
  getMainState,
  refreshRoomCardCount,
  updateRentRoomList,
} from '@/redux/actions/reservation/checkOut';
import SettleDrawer, { SettleDrawerRef } from '@/components/Reservation/SettleDrawer';
import { ReducerState, RentRecordInfo, storeName } from '@/redux/reducers/reservation/checkOut';
import ThrottleButton from '@/components/Reservation/ThrottleButton';

import RoomGuestList from './components/RoomGuestList';
import RentRoomList from './components/RentRoomList';
import OvertimePriceAdjustList from './components/OvertimePriceAdjustList';
import PeripheralReturnList from './components/PeripheralReturnList';
import * as helper from './helper';
import validator from './validator';
import styles from './index.module.scss';

interface IConfig {
  mainOrderId: string; // 主单id
  recList: Array<string>; // 接待单列表
  onEnd?: (result: boolean) => void;
}

const stateSelector = (state: ReducerState) => {
  const { isInit, isLoading } = state;
  return { isInit, isLoading };
};
const mapStateToProps = createStateSelector<ReducerState>(storeName, stateSelector);
const mapDispatchToProps = {
  callInitPage: initPageState,
  callSetLoading: setLoadingStatus,
  callGetState: getMainState,
  callRefreshCard: refreshRoomCardCount,
  callRefreshRentList: updateRentRoomList,
};

type MapStateProps = ReturnType<typeof stateSelector>;
type MapDispatchProps = typeof mapDispatchToProps;

interface IProps extends MapStateProps, MapDispatchProps, IConfig {
  unmountNode: () => void;
}

const Index: React.FC<IProps> = (props) => {
  const { unmountNode, onEnd, isInit, isLoading, callSetLoading } = props;
  const confirmPortal = usePortalConfirm();
  const settleDrawer = useRef<SettleDrawerRef>(null);

  // 冲调原因
  const [reasons, setReasons] = useState<any[]>([]);

  // 数据初始化
  useEffect(() => {
    helper.getReverseReasons().then((list) => {
      setReasons(list);
    });
    const { mainOrderId, recList, callInitPage } = props;
    if (mainOrderId && recList && recList.length) {
      callInitPage(() => {
        return helper.getCheckOutInfo(recList).catch((e) => {
          unmountNode();
          throw e;
        });
      });
    } else {
      message.error(intl.get('reservation_error_config_invalid'));
      unmountNode();
    }
  }, []);

  // 取消退房
  const handleEditCancel = () => {
    unmountNode();
    if (typeof onEnd === 'function') {
      onEnd(false);
    }
  };

  // 校验是否未查房
  const handleCheckErr: (response) => void = async (response) => {
    await showWardRoundConfirm(confirmPortal, response, 'checkOut');
  };

  // 校验房卡是否已退
  const handleCardErr: (response) => void = async (response) => {
    await showRoomCardConfirm(confirmPortal, response);
  };

  // 退房二次确认
  const showConfirmPortal = () => {
    return showCheckOutPrompt(confirmPortal).then((shouldContinue) => {
      if (shouldContinue) return;
      throw new Error('Abort');
    });
  };

  // 校验结账状态
  const checkSettleStatus: (orderId: string) => Promise<void> = async (orderId) => {
    const isUnSettled = await showSettlePrompt(confirmPortal, orderId);
    if (isUnSettled) {
      // 未结账
      await new Promise<void>((resolve) => {
        settleDrawer.current.showDrawer(orderId, resolve);
      });
    }
  };

  const getOverTimePriceRemark = (indexId: string, overtimePriceAdjustList: any[]) => {
    const item = overtimePriceAdjustList?.find((obj) => obj.indexId === indexId);
    if (item) {
      const reasonText = reasons?.find((re) => re.value === item.adjustReason)?.label;
      return `${reasonText}${item.adjustRemarks ? '，' : ''}${item.adjustRemarks || ''}`;
    }
    return null;
  };

  // 提交退房
  const handleSubmitClick = () => {
    const { callGetState, mainOrderId } = props;
    callGetState(async (state) => {
      const { rentRoomList, overtimePriceAdjustList, peripheralRoomInventoryList } = state;
      const err = validator(state);
      if (err) {
        message.error(err);
        return;
      }
      callSetLoading(true);
      // 所有单子都是同一个主单
      try {
        const params = {
          resvNo: mainOrderId,
          rooms: rentRoomList.map((item) => {
            const overTimePriceRemark = getOverTimePriceRemark(
              item.indexId,
              overtimePriceAdjustList,
            );
            return {
              overTimePrice: parseYuan2Fen(item.overtimePrice),
              overTime: item.overtime != null ? parseSeconds2Hour(item.overtime) : item.overtime,
              recNo: item.recNo,
              roomId: item.roomId,
              roomNo: item.roomNo,
              overTimePriceReceivable: item.overtimePriceReceivable
                ? parseYuan2Fen(item.overtimePriceReceivable)
                : 0,
              overTimePriceRemark,
            };
          }),
          peripheralSkuQtyItems: (peripheralRoomInventoryList || []).map((item) => {
            return {
              qty: item.returnQty,
              roomId: item.roomId,
              skuCode: item.skuCode,
              times: item.totalTimes,
            };
          }),
        };
        const validateErr = await checkInService.checkOutValidate(params).then(
          () => null,
          (errRes) => errRes,
        );
        if (validateErr) {
          await handleCheckErr(validateErr);
          await handleCardErr(validateErr);
          throw validateErr;
        }
        await showConfirmPortal();
        await checkInService.createCheckOut(params);
        await checkSettleStatus(mainOrderId);
        unmountNode();
        message.success(intl.get('drawer_checkout_message_checkout_success'));
        if (typeof onEnd === 'function') {
          onEnd(true);
        }
      } catch (e) {
        //
      }
      callSetLoading(false);
    });
  };

  // 刷新房卡信息
  const refreshRoomCardInfo = async () => {
    const { recList, callRefreshCard } = props;
    callSetLoading(true);
    try {
      const res = await checkInService.getCheckOutInfo({ recNos: recList });
      const roomList: RentRecordInfo[] = res.data.checkOutRoomList || [];
      const cardCountMap = {}; // 房间id 房卡数量 map
      roomList.forEach((room) => {
        cardCountMap[room.roomId] = room.roomCardCount;
      });
      callRefreshCard(cardCountMap);
    } catch (_) {
      //
    }
    callSetLoading(false);
  };

  // 刷新订单信息
  const refreshOrderDetail = async () => {
    const { recList, callRefreshRentList } = props;
    callSetLoading(true);
    try {
      const orderDetail = await helper.getCheckOutInfo(recList);
      callRefreshRentList(orderDetail.rentRoomList);
    } catch (_) {
      //
    }
    callSetLoading(false);
  };

  if (!isInit) return <PortalLoading />;

  return (
    <Spin spinning={isLoading}>
      <DrawerHeader title={intl.get('drawer_checkout_title')} onClose={handleEditCancel} />
      <div className={styles.pageContainer}>
        <RoomGuestList />
        <RentRoomList
          refreshOrderDetail={refreshOrderDetail}
          refreshRoomCardInfo={refreshRoomCardInfo}
        />
        <OvertimePriceAdjustList adjustReasons={reasons} />
        <PeripheralReturnList />
      </div>
      <DrawerFooter>
        <ThrottleButton type="primary" className={styles.submitBtn} onClick={handleSubmitClick}>
          {intl.get('drawer_checkout_submit_btn')}
        </ThrottleButton>
        <Button onClick={handleEditCancel}>{intl.get('app_confirm_cancel_btn')}</Button>
      </DrawerFooter>
      <SettleDrawer ref={settleDrawer} />
    </Spin>
  );
};

export default () =>
  usePortalDrawer<IConfig>(connect(mapStateToProps, mapDispatchToProps)(memo(Index)));
